import { useEffect, useState } from 'react';
import { databaseApi } from '../services/api';
import toast from 'react-hot-toast';
import { Card, Progress, Badge, Button } from 'flowbite-react';
import {
  CircleStackIcon,
  ServerIcon,
  BoltIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { trackRender } from '../utils/debugRender';

export default function Database() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  // 🔍 DEBUG: Track renders to identify infinite loops
  trackRender('Database', { loading, optimizing });

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      setLoading(true);
      const data = await databaseApi.stats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load database stats:', error);
      toast.error('Failed to load database statistics');
    } finally {
      setLoading(false);
    }
  }

  async function handleOptimize() {
    setOptimizing(true);
    // TODO: Implement optimization modal and API call
    toast.success('Optimization feature coming soon');
    setTimeout(() => setOptimizing(false), 1000);
  }

  // Calculate storage percentage (mock data for now)
  const storageUsedGB = parseFloat(stats?.database?.totalSize?.replace(/[^\d.]/g, '') || 0);
  const storageLimitGB = 10; // Mock limit
  const storagePercentage = Math.min((storageUsedGB / storageLimitGB) * 100, 100);

  // Mock connection data
  const activeConnections = 12;
  const maxConnections = 100;
  const connectionPercentage = (activeConnections / maxConnections) * 100;

  // Mock query performance
  const avgQueryTime = 45; // ms
  const slowQueries = 3;

  if (loading) {
    return <DatabaseSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Database Statistics</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Storage and performance metrics</p>
        </div>
        <div className="flex gap-3">
          <Button color="gray" onClick={loadStats}>
            <ArrowPathIcon className="mr-2 h-5 w-5" />
            Refresh
          </Button>
          <Button
            color="purple"
            onClick={handleOptimize}
            disabled={optimizing}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <WrenchScrewdriverIcon className="mr-2 h-5 w-5" />
            Optimize Database
          </Button>
        </div>
      </div>

      {/* Hero Stats Card */}
      <Card className="bg-gradient-to-r from-blue-500 to-purple-600 border-0 shadow-xl">
        <div className="flex items-center gap-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            <CircleStackIcon className="h-10 w-10 text-white" />
          </div>
          <div className="flex-1 text-white">
            <h2 className="text-5xl font-bold">{stats?.database?.totalSize || 'N/A'}</h2>
            <p className="mt-1 text-xl opacity-90">Total Database Size</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
            <p className="text-3xl font-bold text-white">
              {stats?.database?.totalUsers?.toLocaleString() || 0}
            </p>
            <p className="mt-1 text-white/80">Total Users</p>
          </div>
          <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
            <p className="text-3xl font-bold text-white">
              {stats?.database?.totalDataEntries?.toLocaleString() || 0}
            </p>
            <p className="mt-1 text-white/80">Data Entries</p>
          </div>
          <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
            <p className="text-3xl font-bold text-white">{Object.keys(stats?.tables || {}).length}</p>
            <p className="mt-1 text-white/80">Tables</p>
          </div>
        </div>
      </Card>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Storage Usage */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500">
                <ServerIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Storage Usage</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {storageUsedGB.toFixed(2)} GB / {storageLimitGB} GB
                </p>
              </div>
            </div>
            <Badge color={storagePercentage > 80 ? 'failure' : storagePercentage > 60 ? 'warning' : 'success'} size="lg">
              {storagePercentage.toFixed(0)}%
            </Badge>
          </div>
          <div className="mt-4">
            <Progress
              progress={storagePercentage}
              size="lg"
              color={storagePercentage > 80 ? 'red' : storagePercentage > 60 ? 'yellow' : 'green'}
              labelProgress
            />
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600 dark:text-gray-400">
              <span>Avg per User:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {stats?.database?.totalUsers
                  ? `${(storageUsedGB * 1024 / stats.database.totalUsers).toFixed(2)} MB`
                  : 'N/A'}
              </span>
            </div>
          </div>
        </Card>

        {/* Connection Status */}
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500">
                <BoltIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Active Connections</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {activeConnections} / {maxConnections} connections
                </p>
              </div>
            </div>
            <Badge color={connectionPercentage > 80 ? 'failure' : connectionPercentage > 60 ? 'warning' : 'success'} size="lg">
              {connectionPercentage.toFixed(0)}%
            </Badge>
          </div>
          <div className="mt-4">
            <Progress
              progress={connectionPercentage}
              size="lg"
              color={connectionPercentage > 80 ? 'red' : connectionPercentage > 60 ? 'yellow' : 'blue'}
              labelProgress
            />
          </div>
          <div className="mt-4 space-y-2">
            <ConnectionHealthIndicator label="Database" status="healthy" />
            <ConnectionHealthIndicator label="PostgREST" status="healthy" />
            <ConnectionHealthIndicator label="RLS Policies" status="healthy" />
          </div>
        </Card>

        {/* Query Performance */}
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500">
              <ChartBarIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Query Performance</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Real-time metrics</p>
            </div>
          </div>
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between rounded-lg bg-white/50 p-4 dark:bg-gray-800/50">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Average Query Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{avgQueryTime}ms</p>
              </div>
              <Badge color={avgQueryTime > 100 ? 'failure' : avgQueryTime > 50 ? 'warning' : 'success'} size="lg">
                {avgQueryTime > 100 ? 'Slow' : avgQueryTime > 50 ? 'Fair' : 'Fast'}
              </Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-white/50 p-4 dark:bg-gray-800/50">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Slow Queries (&gt;1s)</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{slowQueries}</p>
              </div>
              <Badge color={slowQueries > 10 ? 'failure' : slowQueries > 5 ? 'warning' : 'info'} size="lg">
                {slowQueries > 10 ? 'High' : slowQueries > 5 ? 'Medium' : 'Low'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Storage Info */}
        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500">
              <CircleStackIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Storage Details</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Database metrics</p>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            <StorageInfoRow label="Database Size" value={stats?.database?.totalSize || 'N/A'} />
            <StorageInfoRow
              label="Avg per User"
              value={
                stats?.database?.totalUsers
                  ? `${(storageUsedGB * 1024 / stats.database.totalUsers).toFixed(2)} MB`
                  : 'N/A'
              }
            />
            <StorageInfoRow
              label="Total Tables"
              value={Object.keys(stats?.tables || {}).length.toString()}
            />
            <StorageInfoRow
              label="Last Updated"
              value={new Date(stats?.timestamp).toLocaleString()}
            />
          </div>
        </Card>
      </div>

      {/* Tables Breakdown */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <CircleStackIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Tables</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats?.tables &&
            Object.entries(stats.tables).map(([tableName, tableInfo]) => (
              <TableCard key={tableName} name={tableName} info={tableInfo} />
            ))}
        </div>
      </Card>
    </div>
  );
}

function TableCard({ name, info }) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{name}</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {info.rows?.toLocaleString() || 0}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">rows</p>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
          <CircleStackIcon className="h-5 w-5 text-blue-600 dark:text-blue-300" />
        </div>
      </div>
    </Card>
  );
}

function ConnectionHealthIndicator({ label, status }) {
  const statusConfig = {
    healthy: {
      color: 'success',
      icon: CheckCircleIcon,
      text: 'Healthy'
    },
    warning: {
      color: 'warning',
      icon: ExclamationCircleIcon,
      text: 'Warning'
    },
    error: {
      color: 'failure',
      icon: ExclamationCircleIcon,
      text: 'Error'
    }
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div className="flex items-center justify-between rounded-lg bg-white/50 p-3 dark:bg-gray-800/50">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-green-500" />
        <Badge color={config.color}>{config.text}</Badge>
      </div>
    </div>
  );
}

function StorageInfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white/50 p-3 dark:bg-gray-800/50">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

function DatabaseSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <div className="h-8 w-64 bg-gray-300 rounded"></div>
          <div className="mt-2 h-4 w-48 bg-gray-200 rounded"></div>
        </div>
        <div className="flex gap-3">
          <div className="h-10 w-24 bg-gray-300 rounded-lg"></div>
          <div className="h-10 w-48 bg-gray-300 rounded-lg"></div>
        </div>
      </div>

      {/* Hero Card Skeleton */}
      <div className="rounded-lg bg-gradient-to-r from-gray-300 to-gray-400 p-8 shadow-xl">
        <div className="flex items-center gap-6">
          <div className="h-20 w-20 rounded-full bg-white/20"></div>
          <div className="flex-1">
            <div className="h-12 w-32 bg-white/20 rounded"></div>
            <div className="mt-2 h-6 w-48 bg-white/20 rounded"></div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg bg-white/10 p-4 h-24"></div>
          ))}
        </div>
      </div>

      {/* Two-Column Grid Skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg bg-gray-200 p-6 h-64"></div>
        ))}
      </div>

      {/* Tables Skeleton */}
      <div className="rounded-lg bg-gray-200 p-6">
        <div className="h-6 w-32 bg-gray-300 rounded mb-6"></div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rounded-lg bg-gray-300 h-28"></div>
          ))}
        </div>
      </div>
    </div>
  );
}
